import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { Container, Alert, Button, ConfirmButton, Heading, Stack, Text, FormControl, Input, Textarea, EmptyCard, Box } from '@/components/lib'
import api from '../../api'
import utils from '@/utils/utils'
import useStore from '../../store'
import useTitle from '../../hooks/useTitle'

export default function NewTrial () {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState()
  const [loading, setLoading] = useState(false)
  const [demoLoading, setDemoLoading] = useState(false)
  const navigate = useNavigate()
  const { search } = useLocation()
  const { user, updateUser, team, updateTrial } = useStore()
  useTitle('New Trial')

  useEffect(() => {
    if (search) {
      const params = new URLSearchParams(search)
      const name = params.get('name')
      if (name) setName(name)
    }
  }, [search])

  function createTrial () {
    setError()
    setLoading(true)
    api.teams.createTrial(user.team, { name, description }, trial => {
      updateTrial(trial._id, trial) // Ensure trial added to the store
      api.users.me(u => updateUser(u._id, u)) // Refresh user to get new trial permissions
      api.users.createEmailSubscription(user._id, `trial.${trial._id}.participantMessages`, ({ subscriptions }) => updateUser(user._id, { subscriptions }))
      navigate(`/trials/${trial._id}/manage`)
      setLoading(false)
      utils.toast('success', 'Trial created successfully')
    }, err => {
      setError(err.message)
      setLoading(false)
    })
  }

  // Root only: builds a ready-made "Demo Study" — forms, participants, notes,
  // data and a dashboard — so an account can be shown off without assembling
  // one by hand first.
  function createDemoTrial () {
    setError()
    setDemoLoading(true)
    api.root.createDemoTrial(({ trial, warnings }) => {
      updateTrial(trial._id, trial)
      api.users.me(u => updateUser(u._id, u))
      navigate(`/trials/${trial._id}/manage`)
      setDemoLoading(false)
      utils.toast('success', 'Demo study created')
      for (const warning of warnings || []) utils.toast('warning', warning)
    }, err => {
      setError(err.message)
      setDemoLoading(false)
    })
  }

  return (
    <Container>
      <Stack direction={{ base: 'column', md: 'row' }} gap={10}>
        <Box>
          <EmptyCard icon='research' title='Trials' description='A trial (or "project") is a space for defining your study and collecting data. Once created you can add other staff to help manage or collect your data, define your variables, setup your forms, and manage participants.' />
        </Box>
        <Stack flex={1} gap={5} p={5} bg='bg.surface' rounded='md' borderWidth='1px' borderColor='border.default'>
          <Heading as='h2' size='lg'>Create a new trial</Heading>
          <Text>This trial will be managed by the {team && team.name} team.</Text>

          <FormControl label='Trial name'>
            <Input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder='Intervention or trial name' />
          </FormControl>

          <FormControl label='Trial description'>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder='This trial aims to...' />
          </FormControl>
          <Alert variant='left-accent' status='info'>Please note that this information can be changed at any time.</Alert>

          {error &&
            <Alert variant='left-accent' status='error'>There was a problem creating your trial: {error}</Alert>}

          <Stack direction='row' justifyContent='end' alignItems='center'>
            <Button variant='ghost' onClick={() => navigate('/trials')}>Cancel</Button>
            <Button isLoading={loading} colorPalette='primary' onClick={createTrial}>Create trial</Button>
          </Stack>

          {utils.hasPermission(user, 'root', 'global') &&
            <Stack gap={3} pt={5} borderTopWidth='1px' borderColor='border.default'>
              <Box>
                <Heading as='h3' size='md'>Create a demo study</Heading>
                <Text color='fg.muted'>Root only. Creates a ready-made "Demo Study" in the {team && team.name} team: a screening questionnaire, a baseline questionnaire, a weekly diary and an adverse event form, ten participants with notes and sample data, a sample privacy statement, and a dashboard. It takes a few seconds.</Text>
              </Box>
              <Stack direction='row' justifyContent='end'>
                <ConfirmButton
                  header='Create a demo study?'
                  body={`This adds a fully-populated demo trial to the ${team ? team.name : 'current'} team. Delete it from the trial's settings when you have finished with it.`}
                  actionColor='primary'
                  trigger={<Button isLoading={demoLoading} variant='outline' colorPalette='primary'>Create demo study</Button>}
                  onConfirm={createDemoTrial}
                />
              </Stack>
            </Stack>}
        </Stack>
      </Stack>
    </Container>
  )
}
